/**
 * Every project page sits behind this: it loads the project once (the switcher,
 * settings and permission checks reuse that query) and stops at a clear message
 * when the project does not exist or the caller holds no role in it — the
 * backend answers both with 404, so the screen does not guess which.
 */

import { useEffect, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router";
import { Logo } from "@/components/logo";
import { ErrorState } from "@/components/ui/states";
import { useCurrentUser, useLogout } from "@/features/auth/hooks";
import { UsersScreen } from "@/features/users/users-screen";
import { ApiError } from "@/lib/api";
import { forgetProject, rememberProject } from "@/lib/project-url";
import { useProject } from "./hooks";

export function Centered({ children }: { children: ReactNode }) {
  return <div className="grid min-h-screen place-items-center bg-subtle px-6">{children}</div>;
}

/** The pulsing logo, full screen, while a project (or which one to open) loads. */
export function LoadingScreen({ label }: { label: string }) {
  return (
    <Centered>
      <span role="status" aria-label={label} className="animate-pulse">
        <Logo size={36} />
      </span>
    </Centered>
  );
}

/** "Signed in as x · Sign out": a wrong account is the usual reason for no access. */
function SignedInAs() {
  const user = useCurrentUser().data;
  const logout = useLogout();
  if (!user) return null;
  return (
    <p className="text-xs text-muted">
      Signed in as {user.email} ·{" "}
      <button type="button" className="link" disabled={logout.isPending} onClick={() => logout.mutate()}>
        Sign out
      </button>
    </p>
  );
}

/** Back to `/`, which opens another project you can. Out of this project's
 *  router, so a plain link: a full page load. */
const goBack = (primary: boolean) => (
  <a className={`btn ${primary ? "btn-primary" : ""}`} href="/">
    Back to your projects
  </a>
);

export function ProjectNotFound() {
  const email = useCurrentUser().data?.email;
  return (
    <Centered>
      <div className="box flex max-w-md flex-col items-center gap-3 px-7 py-6 text-center">
        <Logo size={32} />
        <h1 className="text-[15px] font-semibold">You can't open this project</h1>
        <p className="text-[13px] leading-relaxed text-muted">
          It doesn't exist, or you haven't been added. Ask the person who shared the link to add{" "}
          {email ?? "your email"}.
        </p>
        {goBack(true)}
        <SignedInAs />
      </div>
    </Centered>
  );
}

function ProjectFailed({ error }: { error: unknown }) {
  return (
    <Centered>
      <div className="flex max-w-md flex-col items-center gap-3">
        <ErrorState error={error} what="the project" />
        <div className="flex gap-2">
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            Reload
          </button>
          {goBack(false)}
        </div>
      </div>
    </Centered>
  );
}

export function ProjectGate() {
  const { slug, query } = useProject();
  const { pathname } = useLocation();
  const status = query.error instanceof ApiError ? query.error.status : undefined;
  const closed = status === 404 || status === 403;
  const name = query.data?.name;

  useEffect(() => {
    if (!slug) return;
    // Old unprefixed links and `/` go to the last project that actually opened…
    if (query.isSuccess) rememberProject(slug);
    // …and never again to one that is gone or closed to you.
    if (closed) forgetProject(slug);
  }, [query.isSuccess, closed, slug]);

  useEffect(() => {
    if (!name) return;
    document.title = `${name} · Agentic Workbench`;
    return () => void (document.title = "Agentic Workbench");
  }, [name]);

  // The session ran out after the page loaded: sign in again, then come back.
  useEffect(() => {
    if (status !== 401) return;
    const { pathname, search, hash } = window.location;
    window.location.replace(`/login?next=${encodeURIComponent(`${pathname}${search}${hash}`)}`);
  }, [status]);

  if (query.isPending || status === 401) return <LoadingScreen label="Loading project" />;
  // An admin need not be in the project to manage people: Users stands alone.
  if (closed && pathname === "/users") return <UsersScreen />;
  if (closed) return <ProjectNotFound />;
  if (query.isError) return <ProjectFailed error={query.error} />;
  return <Outlet />;
}
